/**
 * @jeraisonne/db — types & helpers purs Alwaysdata.
 *
 * Types partagés app ⇄ edge fns + schémas Zod + mappers PURS testés.
 * Un secret ne transite jamais dans ces types (le token vit dans Vault).
 *
 * Doc API : https://api.alwaysdata.com/doc/ (ressources `mailbox`, `domain`).
 * On ne manipule QUE les mailboxes du domaine configuré (par défaut
 * `jeraisonne.fr`) : le filtrage est fait côté edge fn via l'ID du domaine.
 */

import { z } from 'zod';

// ─── Config (row publique renvoyée au client, jamais le token) ───────────────

/** Vue admin de la config (sans le token). */
export interface AlwaysdataConfig {
  provider: 'alwaysdata';
  api_url: string;
  domain_name: string;
  allowed_ips: string | null;
  token_set: boolean;
  updated_at: string;
}

/** Patch de config admin (le token est optionnel : s'il est fourni → Vault). */
export const alwaysdataConfigPatchSchema = z
  .object({
    api_url: z
      .string()
      .trim()
      .url()
      .max(200)
      .optional()
      .refine((v) => v === undefined || /^https?:\/\//.test(v), {
        message: 'api_url doit commencer par http(s)://',
      }),
    domain_name: z
      .string()
      .trim()
      .toLowerCase()
      .min(3)
      .max(253)
      .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, {
        message: 'nom de domaine invalide',
      })
      .optional(),
    allowed_ips: z.string().trim().max(500).nullable().optional(),
    /** Si fourni non-vide → stocké dans Vault (write-only, jamais renvoyé). */
    token: z.string().trim().min(8).max(200).optional(),
  })
  .strict();

export type AlwaysdataConfigPatch = z.infer<typeof alwaysdataConfigPatchSchema>;

// ─── Mailbox (schéma miroir de la ressource Alwaysdata /v1/mailbox) ──────────

/**
 * Vue « redirection » d'une mailbox — seuls les champs que l'écran admin gère.
 * On garde `password` et les blocs antispam/purge/quota côté API mais on ne les
 * expose pas dans l'UI (portée validée : redirect + create/delete).
 */
export interface AlwaysdataMailbox {
  id: number;
  href: string;
  /** Partie locale (avant @). Wildcard `*` supporté par Alwaysdata (catch-all). */
  name: string;
  /** Domaine complet reconstruit côté edge fn depuis /v1/domain/. */
  domain_name: string;
  redirect_enabled: boolean;
  /** Adresses cibles, séparées par espace ou newline. Toujours normalisées ici. */
  redirect_to: string;
  redirect_local_copy: boolean;
  redirect_srs: boolean;
  annotation: string | null;
}

/**
 * Représentation « adresse email complète » pour l'UI (name@domain), reconstruite
 * côté edge fn depuis `name` + le domaine résolu par ID.
 */
export function mailboxAddress(mailbox: Pick<AlwaysdataMailbox, 'name' | 'domain_name'>): string {
  return `${mailbox.name}@${mailbox.domain_name}`;
}

// ─── Validation du name (partie locale, wildcard `*` autorisé) ───────────────

/**
 * Partie locale d'une adresse email selon la doc Alwaysdata (« Indiquez ici la
 * partie de l'email précédant le signe @. Vous pouvez utiliser le caractère
 * wildcard *. »). On accepte :
 *   - `*` seul (catch-all), OU
 *   - un nom RFC-simplifié : lettres/chiffres/`._-+`, 1..64 car.
 * Refuse les points en tête/fin et les points consécutifs.
 */
const MAILBOX_NAME_RE = /^(?:\*|[a-z0-9](?:[a-z0-9._+-]{0,62}[a-z0-9])?)$/;

export const mailboxNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(64)
  .regex(MAILBOX_NAME_RE, { message: 'partie locale invalide (a-z, 0-9, . _ - +, ou *)' })
  .refine((v) => !v.includes('..'), { message: 'points consécutifs interdits' });

// ─── Validation d'UNE adresse cible (destination redirect_to) ────────────────

/**
 * Regex volontairement pragmatique (pas RFC 5322 complet) : identique à ce
 * qu'on trouve dans `_shared/newsletter-emails.ts` — cohérence projet.
 */
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const singleEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(EMAIL_RE, { message: 'email invalide' });

/**
 * Parse et normalise la liste des cibles d'une redirection.
 * Alwaysdata accepte plusieurs adresses séparées par espace ou newline —
 * on normalise en une string canonique : une adresse par ligne, sans doublon.
 * Renvoie `null` si l'entrée est vide (ex. redirection désactivée).
 */
export function normalizeRedirectTo(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = input
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const addr of raw) {
    if (seen.has(addr)) continue;
    seen.add(addr);
    out.push(addr);
  }
  return out.length ? out.join('\n') : null;
}

export const redirectToSchema = z
  .string()
  .trim()
  .max(4000)
  .transform((v, ctx) => {
    const normalized = normalizeRedirectTo(v);
    if (!normalized) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'au moins une adresse email est requise',
      });
      return z.NEVER;
    }
    for (const addr of normalized.split('\n')) {
      if (!singleEmailSchema.safeParse(addr).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `email invalide : ${addr}`,
        });
        return z.NEVER;
      }
    }
    return normalized;
  });

// ─── Payload d'update / create côté UI ───────────────────────────────────────

export const mailboxCreateSchema = z
  .object({
    name: mailboxNameSchema,
    redirect_to: redirectToSchema,
    redirect_enabled: z.boolean().optional().default(true),
    redirect_local_copy: z.boolean().optional().default(false),
    redirect_srs: z.boolean().optional().default(true),
    annotation: z.string().trim().max(200).optional().nullable(),
  })
  .strict();

export type MailboxCreateInput = z.infer<typeof mailboxCreateSchema>;

/**
 * Patch : redirect_to est optionnel (on peut juste toggler redirect_enabled).
 * S'il est fourni, on le normalise ; s'il est absent ou vide, on ne le change pas.
 */
export const mailboxUpdateSchema = z
  .object({
    redirect_enabled: z.boolean().optional(),
    redirect_to: redirectToSchema.optional(),
    redirect_local_copy: z.boolean().optional(),
    redirect_srs: z.boolean().optional(),
    annotation: z.string().trim().max(200).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'aucun champ à mettre à jour' });

export type MailboxUpdateInput = z.infer<typeof mailboxUpdateSchema>;

// ─── Mapper UI → body Alwaysdata (POST/PATCH /v1/mailbox/) ───────────────────

/**
 * Construit le corps POST à envoyer à /v1/mailbox/ pour créer une redirection.
 * `domainRef` = URI courte (`/v1/domain/<ID>/`) — c'est ce que l'API attend.
 * On force explicitement les champs pour être robuste aux défauts serveur qui
 * pourraient changer.
 */
export function buildMailboxCreateBody(
  input: MailboxCreateInput,
  domainRef: string,
): Record<string, unknown> {
  return {
    domain: domainRef,
    name: input.name,
    redirect_enabled: input.redirect_enabled ?? true,
    redirect_to: input.redirect_to,
    redirect_local_copy: input.redirect_local_copy ?? false,
    redirect_srs: input.redirect_srs ?? true,
    ...(input.annotation ? { annotation: input.annotation } : {}),
  };
}

/**
 * Construit le corps PATCH partiel pour /v1/mailbox/<id>/.
 * N'inclut que les champs explicitement fournis (préserve les autres réglages).
 */
export function buildMailboxUpdateBody(input: MailboxUpdateInput): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.redirect_enabled !== undefined) body.redirect_enabled = input.redirect_enabled;
  if (input.redirect_to !== undefined) body.redirect_to = input.redirect_to;
  if (input.redirect_local_copy !== undefined) body.redirect_local_copy = input.redirect_local_copy;
  if (input.redirect_srs !== undefined) body.redirect_srs = input.redirect_srs;
  if (input.annotation !== undefined) body.annotation = input.annotation;
  return body;
}

// ─── Authorization header (Basic auth Alwaysdata : `TOKEN:` — deux-points) ───

/**
 * Construit l'en-tête Authorization pour l'API Alwaysdata.
 * L'API attend un Basic auth où le login est le token et le password est vide
 * (d'où le `:` final avant l'encodage base64).
 *
 * `btoa` est disponible partout (edge fn Deno + Node ≥ 16 + navigateurs).
 */
export function alwaysdataAuthHeader(token: string): string {
  const encoded =
    typeof btoa === 'function' ? btoa(`${token}:`) : Buffer.from(`${token}:`).toString('base64');
  return `Basic ${encoded}`;
}
